import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    ColumnDef,
    flexRender,
    SortingState,
    RowSelectionState
} from "@tanstack/react-table";
import { Image } from "react-bootstrap";
import cn from 'classnames';
import { useEffect, useMemo, useState } from "react";
import TableCheckBox from "../../../../../../../component/common/TableCheckBox/TableCheckBox";
import UserCard from "../../../../../../../component/common/UserCard/UserCard";
import CustomButton, { ButtonFont, ButtonSize, ButtonType } from "../../../../../../../component/common/CustomButton/CustomButton";
import { ASSESTS } from "../../../../../../../constant/assest";
import Pagination from "../../../../../../../component/UI/Pagination/Pagination";

type UserRow = {
    userName: string;
    avatar: string;
    location: string;
    city: string;
    zone: string;
    speciesCount: number;
    lastNotification: string;
};

const WaterbodiesCampaignTable = () => {

    const data = useMemo<UserRow[]>(() => [
        {
            userName: "Mike Johnson",
            avatar: "",
            location: "40.7128, -74.0060",
            city: "New York",
            zone: "North West",
            speciesCount: 35,
            lastNotification: "5/15/2024",
        },
        {
            userName: "Carlos Rodriguez",
            avatar: "",
            location: "42.7164, -64.07889",
            city: "New York",
            zone: "North West",
            speciesCount: 28,
            lastNotification: "1/25/2024",
        },
        {
            userName: "Emma Wilson",
            avatar: "",
            location: "41.8781, -87.6298",
            city: "New York",
            zone: "North West",
            speciesCount: 25,
            lastNotification: "3/05/2024",
        },
        {
            userName: "Jenny Samson",
            avatar: "",
            location: "52.8781, -87.6298",
            city: "New York",
            zone: "North West",
            speciesCount: 16,
            lastNotification: "1/8/2024",
        },
    ], []);

    const columns = useMemo<ColumnDef<UserRow>[]>(() => [
        {
            id: "select",
            header: ({ table }) => (
                <TableCheckBox
                    checked={table.getIsAllPageRowsSelected()}
                    indeterminate={table.getIsSomePageRowsSelected()}
                    onChange={table.getToggleAllPageRowsSelectedHandler()}
                />
            ),
            cell: ({ row }) => (
                <TableCheckBox
                    checked={row.getIsSelected()}
                    indeterminate={row.getIsSomeSelected()}
                    disabled={!row.getCanSelect()}
                    onChange={row.getToggleSelectedHandler()}
                />
            ),
            enableSorting: false,
        },
        {
            header: "User Name",
            accessorKey: "userName",
            cell: (info) => {
                const row = info.row.original;
                return (
                    <UserCard
                        userName={row.userName}
                        userHandle=""
                        userImage={row.avatar}
                    />
                );
            },
        },
        { header: "Location", accessorKey: "location" },
        {
            header: "City/Zone",
            accessorKey: "city",
            cell: (info) => {
                const row = info.row.original;
                return (
                    <div className="text-center">
                        <div className={'city'}>{row.city}</div>
                        <div className={'zone'}>{row.zone}</div>
                    </div>
                );
            },
        },
        { header: "Species Count", accessorKey: "speciesCount" },
        { header: "Last Notification", accessorKey: "lastNotification" },
        {
            header: "Action",
            id: "action",
            cell: () => (
                <CustomButton
                    btnType={ButtonType.IMAGE}
                    onClick={() => {}}
                >
                    <Image src={ASSESTS.EDITICON} alt="Edit" width={20} height={20} />
                </CustomButton>
            ),
            enableSorting: false,
        },
    ], []);

    const [sorting, setSorting] = useState<SortingState>([]);
    const [pageSize, setPageSize] = useState(5);
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    const table = useReactTable({
        data,
        columns,
        state: { sorting, rowSelection },
        onSortingChange: setSorting,
        onRowSelectionChange: setRowSelection,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        enableRowSelection: true,
        initialState: { pagination: { pageIndex: 0, pageSize: pageSize } }
    });

    useEffect(() => {
        table.setPageSize(pageSize);
    }, [pageSize, table]);

    return (
        <div className={'tableContainer'}>
            <div className={'tableWrapper'}>
                <table>
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    const isActionColumn = typeof header.column.columnDef.header === "string" && header.column.columnDef.header === "Action";
                                    return (
                                        <th
                                            key={header.id}
                                            onClick={header.column.getToggleSortingHandler()}
                                            style={{
                                                cursor: header.column.getCanSort() ? "pointer" : "default"
                                            }}
                                            className={cn(header.column.columnDef.id && `th-${header.column.columnDef.id}`, isActionColumn ? 'text-center' : undefined, header.column.columnDef.header === "City/Zone" && 'text-center')}
                                        >
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            {header.column.getCanSort() &&
                                                <span className={cn('tableShorting')}>
                                                    <Image
                                                        src={header.column.getIsSorted() === "asc" ? ASSESTS.ASSENDING : header.column.getIsSorted() === "desc" ? ASSESTS.DECENDING : ASSESTS.SHORT}
                                                        alt="Short"
                                                    />
                                                </span>
                                            }
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map((row) => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map((cell) => {
                                    const isActionColumn = typeof cell.column.columnDef.header === "string" && cell.column.columnDef.header === "Action";

                                    return (
                                        <td
                                            key={cell.id}
                                            className={cn(cell.column.columnDef.id && `td-${cell.column.columnDef.id}`, isActionColumn ? 'text-center' : undefined)}
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Pagination
                enteriesShows={`${table.getState().pagination.pageIndex * pageSize + 1}-${Math.min((table.getState().pagination.pageIndex + 1) * pageSize, data.length)} of ${data.length} Entries`}
                pageNumbers={Array.from({ length: table.getPageCount() }, (_, pageIndex) => (
                    <CustomButton
                        key={pageIndex}
                        onClick={() => table.setPageIndex(pageIndex)}
                        disabled={table.getState().pagination.pageIndex === pageIndex}
                        btnType={table.getState().pagination.pageIndex === pageIndex ? ButtonType.ACTIVENOSTYLE : ButtonType.NOSTYLE}
                        btnSize={ButtonSize.NORMAL}
                        btnFont={table.getState().pagination.pageIndex === pageIndex ? ButtonFont.BOLD : ButtonFont.NORMAL}
                        className={cn('pageNumberBtn', table.getState().pagination.pageIndex === pageIndex && 'activePageNumberBtn')}
                    >
                        {pageIndex + 1}
                    </CustomButton>
                ))}
                dropDownOptions={[
                    { value: "5", label: "5" },
                    { value: "10", label: "10" },
                    { value: "20", label: "20" },
                    { value: "25", label: "25" },
                ]}
                dropdownOnChange={(value) => { setPageSize(Number(value)); }}
                dropDownPlaceholder={pageSize.toString()}
            />
        </div>
    );
};

export default WaterbodiesCampaignTable;
